import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ecommerceService } from '../../services/ecommerceService';

// Async thunks for API calls
export const fetchProducts = createAsyncThunk(
  'ecommerceProducts/fetchProducts',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getProducts(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchProductDetail = createAsyncThunk(
  'ecommerceProducts/fetchProductDetail',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getProduct(productId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'ecommerceProducts/fetchFeaturedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getFeaturedProducts();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchNewArrivals = createAsyncThunk(
  'ecommerceProducts/fetchNewArrivals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getNewArrivals();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBestSellers = createAsyncThunk(
  'ecommerceProducts/fetchBestSellers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getBestSellers();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOnSaleProducts = createAsyncThunk(
  'ecommerceProducts/fetchOnSaleProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getOnSaleProducts();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const searchProducts = createAsyncThunk(
  'ecommerceProducts/searchProducts',
  async ({ query, autocomplete = false }, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.searchProducts(query, autocomplete);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchRelatedProducts = createAsyncThunk(
  'ecommerceProducts/fetchRelatedProducts',
  async (productId, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getRelatedProducts(productId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'ecommerceProducts/fetchCategories',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await ecommerceService.getCategories(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  products: [],
  productDetail: null,
  featuredProducts: [],
  newArrivals: [],
  bestSellers: [],
  onSaleProducts: [],
  relatedProducts: [],
  categories: [],
  searchResults: [],
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
  filters: {
    category: null,
    department: null,
    minPrice: null,
    maxPrice: null,
    isOnSale: false,
    isFeatured: false,
    isNew: false,
    isBestSeller: false,
    // Opt-in: sending in_stock=true by default hides the whole catalogue on
    // tenants that haven't loaded inventory yet.
    inStock: false,
    search: '',
    ordering: '-created_at',
  },
  loading: false,
  detailLoading: false,
  error: null,
};

const ecommerceProductSlice = createSlice({
  name: 'ecommerceProducts',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearProductDetail: (state) => {
      state.productDetail = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || 0,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Product Detail
      .addCase(fetchProductDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchProductDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.productDetail = action.payload;
      })
      .addCase(fetchProductDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload;
      })

      // Fetch Featured Products
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredProducts = action.payload.results || action.payload;
      })
      .addCase(fetchFeaturedProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch New Arrivals
      .addCase(fetchNewArrivals.fulfilled, (state, action) => {
        state.newArrivals = action.payload.results || action.payload;
      })

      // Fetch Best Sellers
      .addCase(fetchBestSellers.fulfilled, (state, action) => {
        state.bestSellers = action.payload.results || action.payload;
      })

      // Fetch On Sale Products
      .addCase(fetchOnSaleProducts.fulfilled, (state, action) => {
        state.onSaleProducts = action.payload.results || action.payload;
      })

      // Search Products
      .addCase(searchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.results || action.payload;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Related Products
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.relatedProducts = action.payload.results || action.payload;
      })

      // Fetch Categories
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload.results || action.payload;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  clearProductDetail,
  clearSearchResults,
  clearError,
} = ecommerceProductSlice.actions;

export default ecommerceProductSlice.reducer;
